import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Activity } from 'types/interfaces/activity';
import { ACTIVITY_TYPE_CONFIG } from '../constants';
import { AvatarWithFlag } from 'components/AvatarWithFlag/AvatarWithFlag';
import { FoilCardImage } from 'components/FoilCardImage/FoilCardImage';
import { Points } from 'components/Points/Points';
import { formatRelativeTime, formatAccessibleTime } from 'utils/relativeTime';
import './ActivityFeedItem.css';

interface ActivityFeedItemProps {
  activity: Activity;
  isNew?: boolean;
  onAnimationEnd?: (id: string) => void;
}

const ActivityFeedItem: React.FC<ActivityFeedItemProps> = ({
  activity,
  isNew = false,
  onAnimationEnd,
}) => {
  const navigate = useNavigate();
  const config = ACTIVITY_TYPE_CONFIG[activity.type];
  const [isHighlighted, setIsHighlighted] = useState(isNew);

  // Handle entry animation end
  useEffect(() => {
    if (!isNew) return;

    const timer = setTimeout(() => {
      setIsHighlighted(false);
      onAnimationEnd?.(activity.id);
    }, 1800); // 0.3s slide-in + 1.5s highlight fade

    return () => clearTimeout(timer);
  }, [isNew, activity.id, onAnimationEnd]);

  // Card click → navigate to card detail
  const handleCardClick = useCallback(() => {
    navigate(`/cards/${activity.card.id}`);
  }, [navigate, activity.card.id]);

  // Card image error handler — fallback chain
  const handleImageError = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const img = e.currentTarget;
      const currentSrc = img.src;
      const imageComingSoon = 'https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/Search/image-coming-soon.png';

      if (!img.dataset.triedLarge && currentSrc.includes('/normal/')) {
        img.dataset.triedLarge = 'true';
        img.src = currentSrc.replace('/normal/', '/large/');
        return;
      }
      if (!img.dataset.triedSmall && (currentSrc.includes('/large/') || currentSrc.includes('/normal/'))) {
        img.dataset.triedSmall = 'true';
        img.src = currentSrc.replace('/large/', '/small/').replace('/normal/', '/small/');
        return;
      }
      if (img.src !== imageComingSoon) {
        img.src = imageComingSoon;
      }
    },
    []
  );

  return (
    <div
      className={`masonry-card${isNew ? ' masonry-card--entering' : ''}${isHighlighted ? ' masonry-card--highlight' : ''}`}
      style={{
        borderColor: config.borderColor,
        '--glow-color': `${config.color}44`,
      } as React.CSSProperties}
    >
      {/* Hero card image with overlay badges */}
      <div
        className="masonry-card__image-container"
        onClick={handleCardClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') handleCardClick();
        }}
        aria-label={`View ${activity.card.name}`}
      >
        <FoilCardImage
          src={activity.card.imageUrl}
          alt={activity.card.name}
          foil={activity.card.foil}
          loading="lazy"
          onError={handleImageError}
          className="masonry-card__hero-img"
        />
      </div>

      {/* Footer: user info + card info */}
      <div className="masonry-card__footer">
        <div className="masonry-card__footer-row1">
          <Link
            to={`/profile/u/${encodeURIComponent(activity.user.username)}`}
            className="masonry-card__user-link"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="masonry-card__avatar">
              <AvatarWithFlag
                avatarUrl={activity.user.avatarUrl}
                countryCode={activity.user.countryCode}
                username={activity.user.username}
                size={28}
              />
            </div>
            <span className="masonry-card__username">{activity.user.username}</span>
          </Link>
          <time
            className="masonry-card__timestamp"
            dateTime={activity.createdAt}
            title={formatAccessibleTime(activity.createdAt)}
          >
            {formatRelativeTime(activity.createdAt)}
          </time>
          <div
            className="masonry-card__badge-type"
            style={{ backgroundColor: config.color }}
          >
            <FontAwesomeIcon icon={config.icon} className="masonry-card__badge-type-icon" />
            {config.label}
          </div>
        </div>
        <div className="masonry-card__footer-row2">
          <Link
            to={`/cards/${activity.card.id}`}
            className="masonry-card__card-name"
            onClick={(e) => e.stopPropagation()}
          >
            {activity.card.name}
          </Link>
          <span className="masonry-card__set-code">{activity.card.setCode.toUpperCase()}</span>
          <div className="masonry-card__badge-points">
            {activity.pointValue !== null ? (
              <Points points={activity.pointValue} type="small" />
            ) : (
              <span className="masonry-card__no-price">???</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivityFeedItem;
